// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 19-design-system.md
// @greenlight-spec: decisions/0050-serving-subdomain-decoupled-from-slug.md
import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { GLDrawer } from '@/components/greenlight/GLDrawer';
import { api } from '@/lib/api';
import { mutationErrorMessage } from '@/lib/apiErrors';
import { APP_SLUG_REGEX } from '@greenlight/shared';

export interface ChangeUrlDialogProps {
  appId: string;
  appName: string;
  slug: string;
  servingSubdomain: string | null;
  /** The app's current serving FQDN (shared_environment.domain). */
  domain: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Moves the app's public URL to a new subdomain under the apps domain
 * (ADR-0050). The slug — repo, namespace, internal identity — never changes.
 * The old URL is released immediately with no redirect, so the copy says so
 * before the user confirms.
 */
export function ChangeUrlDialog({
  appId,
  appName,
  slug,
  servingSubdomain,
  domain,
  open,
  onOpenChange,
}: ChangeUrlDialogProps): React.JSX.Element {
  const queryClient = useQueryClient();
  const mutation = api.apps.update.useMutation();
  const current = servingSubdomain ?? slug;
  const [value, setValue] = React.useState(current);
  const [error, setError] = React.useState<string | null>(null);
  const draftSession = React.useRef({ appId, open: false });

  React.useEffect(() => {
    if (open && (!draftSession.current.open || draftSession.current.appId !== appId)) {
      setValue(current);
      setError(null);
    }
    draftSession.current = { appId, open };
  }, [open, current, appId]);

  // The apps-domain suffix everything serves under — the current FQDN minus
  // its first label.
  const suffix = domain.split('.').slice(1).join('.');

  const trimmed = value.trim();
  const unchanged = trimmed === current;
  const invalid = trimmed.length > 0 && !APP_SLUG_REGEX.test(trimmed);

  const submit = async (): Promise<void> => {
    setError(null);
    try {
      const res = await mutation.mutateAsync({
        params: { id: appId },
        body: { serving_subdomain: trimmed },
      });
      if (res.status >= 300) throw res;
      toast.success(`${appName} now serves at ${trimmed}.${suffix}`);
      onOpenChange(false);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['apps.get', appId] }),
        queryClient.invalidateQueries({ queryKey: ['apps'] }),
      ]);
    } catch (err) {
      setError(mutationErrorMessage(err));
    }
  };

  return (
    <GLDrawer
      variant="confirm"
      mobileBreakpoint={768}
      open={open}
      onOpenChange={onOpenChange}
      preventDismiss={mutation.isPending}
      title={`Change the URL of ${appName}?`}
      description={
        <>
          The current URL <span className="font-mono wrap-anywhere">{domain}</span> stops working
          immediately — there is no redirect, so shared links and bookmarks must be updated. This
          change is audited.
        </>
      }
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            onClick={() => void submit()}
            disabled={unchanged || invalid || trimmed.length === 0 || mutation.isPending}
          >
            {mutation.isPending ? 'Changing…' : 'Change URL'}
          </Button>
        </>
      }
    >
      <div className="space-y-2">
        <div className="flex items-center gap-1.5">
          <Input
            value={value}
            aria-label="New subdomain"
            aria-invalid={invalid || error !== null}
            onChange={(e) => {
              setValue(e.target.value);
              setError(null);
            }}
            placeholder={current}
            className="font-mono"
          />
          <span className="shrink-0 text-sm text-muted-foreground">.{suffix}</span>
        </div>
        {invalid && (
          <p className="text-sm text-gl-red-text" role="alert">
            Lowercase letters, digits, and hyphens; must start with a letter (3–42 characters).
          </p>
        )}
        {error && (
          <p className="text-sm text-gl-red-text" role="alert">
            {error}
          </p>
        )}
      </div>
    </GLDrawer>
  );
}
